import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, AlertOctagon, Download, Gauge, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const INPUT = 'h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3]';
const LABEL = 'block text-[11px] font-semibold text-[#706E6B] mb-1';
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

// Client-side CSV: every panel exports exactly the rows it shows.
function exportCsv(filename, head, rows) {
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /^[=+\-@]/.test(s) ? `'${s}` : /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const blob = new Blob([[head, ...rows].map((r) => r.map(esc).join(',')).join('\n')], { type: 'text/csv' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

function Panel({ title, onExport, children, className }) {
  return (
    <section className={cn('bg-white border border-[#D8DDE6] rounded-lg p-4 flex flex-col gap-3', className)}>
      <div className="flex items-center justify-between">
        <h2 className="text-[14px] font-semibold text-[#2E2E2E]">{title}</h2>
        {onExport && (
          <button type="button" onClick={onExport} className="text-[12px] text-[#0176D3] hover:underline flex items-center gap-1">
            <Download size={13} /> CSV
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value, hint, tone }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-[#706E6B]">{label}</div>
      <div className={cn('text-[20px] font-semibold tabular-nums truncate', tone === 'bad' ? 'text-[#BA0517]' : tone === 'good' ? 'text-[#2E844A]' : 'text-[#2E2E2E]')}>{value}</div>
      {hint && <div className="text-[11px] text-[#706E6B]">{hint}</div>}
    </div>
  );
}

const Stats = ({ children }) => <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">{children}</div>;

function MiniTable({ head, rows }) {
  if (!rows.length) return <p className="text-[12px] text-[#706E6B]">Nothing to show for this period.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px] tabular-nums">
        <thead><tr className="text-[#706E6B] text-left">{head.map((h, i) => <th key={h} className={cn('py-1 font-medium', i && 'text-right')}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, ri) => <tr key={ri} className="border-t border-[#F3F2F2]">{r.map((c, i) => <td key={i} className={cn('py-1.5', i && 'text-right')}>{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function AgeingChart({ buckets, money }) {
  return (
    <div className="h-44">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={buckets} margin={{ left: 8, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF1F6" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} width={70} tickFormatter={(v) => money(v, true)} />
          <Tooltip formatter={(v) => money(v)} />
          <Bar dataKey="amount" name="Amount" fill="#0176D3" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function FinanceDashboardPage() {
  const today = new Date();
  const [from, setFrom] = useState(ymd(new Date(today.getFullYear(), today.getMonth() - 11, 1)));
  const [to, setTo] = useState(ymd(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/finance-reports/dashboard', { params: { from, to } });
      setData(res.data);
    } catch (e) {
      alertService.error('Could not load the dashboard', e?.response?.data?.message || 'Please try again');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const cur = data?.currency || 'INR';
  const money = (v, compact) => (compact
    ? new Intl.NumberFormat('en-IN', { notation: 'compact', maximumFractionDigits: 1 }).format(v || 0)
    : formatCurrency(v || 0, cur));
  const tag = `${from}_to_${to}`;

  return (
    <div className={PAGE}>
      <PageHeader title="Finance Dashboard" subtitle={<>From the ledger, in {cur}. Foreign-currency entries use the rate stored when each one was posted.</>} icon={Gauge} actions={<>
        <div className="flex items-end gap-2">
          <div><label className={LABEL}>From</label><input type="date" className={INPUT} value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><label className={LABEL}>To</label><input type="date" className={INPUT} value={to} min={from} onChange={(e) => setTo(e.target.value)} /></div>
          <button type="button" onClick={load} className="h-9 px-3 border border-[#C9C9C9] rounded bg-white text-[13px] flex items-center gap-1 hover:bg-[#F3F3F3] whitespace-nowrap shrink-0" aria-label="Refresh">
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </button>
        </div>
      </>} />

      {!data && loading && <p className="text-[13px] text-[#706E6B]">Loading…</p>}

      {data && (
        <>
          {data.alerts.length > 0 && (
            <div className="flex flex-col gap-2">
              {data.alerts.map((a) => (
                <div key={a.code} className={cn('flex items-center gap-2 px-3 py-2 rounded border text-[13px]',
                  a.level === 'critical' ? 'bg-[#FEF1EE] border-[#F8B6A8] text-[#8C1B0F]' : 'bg-[#FEF8E8] border-[#F5D58B] text-[#7A5300]')}>
                  {a.level === 'critical' ? <AlertOctagon size={15} /> : <AlertTriangle size={15} />}
                  {a.message}
                </div>
              ))}
            </div>
          )}

          <Panel title="Cash position" onExport={() => exportCsv(`cash-projection_${tag}.csv`, ['Horizon (days)', 'Expected in', 'Due out', 'Projected cash'], data.cash.projection.map((p) => [p.days, p.inflow, p.outflow, p.balance]))}>
            <Stats>
              <Stat label="Cash & bank" value={money(data.cash.balance)} tone={data.cash.balance < 0 ? 'bad' : undefined} />
              <Stat label="Monthly burn" value={data.cash.monthlyBurn > 0 ? money(data.cash.monthlyBurn) : 'None'} hint="Average net outflow, last 3 months" />
              <Stat label="Runway" value={data.cash.runwayMonths === null ? 'Cash positive' : `${data.cash.runwayMonths} months`} tone={data.cash.runwayMonths !== null && data.cash.runwayMonths < 3 ? 'bad' : 'good'} />
              <Stat label="Customer advances" value={money(data.receivables.advances)} hint="Received, not yet applied" />
            </Stats>
            <MiniTable head={['Next', 'Expected in', 'Due out', 'Projected cash']}
              rows={data.cash.projection.map((p) => [`${p.days} days`, money(p.inflow), money(p.outflow),
                <span key="b" className={p.balance < 0 ? 'text-[#BA0517] font-semibold' : ''}>{money(p.balance)}</span>])} />
          </Panel>

          <Panel title="Revenue and profit" onExport={() => exportCsv(`revenue-trend_${tag}.csv`, ['Month', 'Revenue', 'Expenses', 'Profit', 'Cash in', 'Cash out'], data.revenue.trend.map((m) => [m.month, m.revenue, m.expenses, m.profit, m.cashIn, m.cashOut]))}>
            <Stats>
              <Stat label="Revenue (period)" value={money(data.revenue.total)} />
              <Stat label="This month" value={money(data.revenue.thisMonth)} hint={data.revenue.momPct === null ? 'No prior month' : `${data.revenue.momPct > 0 ? '+' : ''}${data.revenue.momPct}% on last month`} />
              <Stat label="Year to date" value={money(data.revenue.ytd)} />
              <Stat label="MRR" value={money(data.revenue.mrr)} hint={`${data.revenue.recurringCount} active recurring invoice(s)`} />
            </Stats>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.revenue.trend.map((m) => ({ ...m, label: monthLabel(m.month) }))} margin={{ left: 8, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF1F6" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={70} tickFormatter={(v) => money(v, true)} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue" name="Revenue" fill="#0176D3" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="expenses" name="Expenses" fill="#FE9339" radius={[3, 3, 0, 0]} />
                  <Line dataKey="profit" name="Profit" stroke="#2E844A" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <Stats>
              <Stat label="Expenses (period)" value={money(data.profitability.expenses)} />
              <Stat label="Net profit" value={money(data.profitability.netProfit)} tone={data.profitability.netProfit < 0 ? 'bad' : 'good'} />
              <Stat label="Net margin" value={pct(data.profitability.marginPct)} />
              <Stat label="Collected (period)" value={money(data.funnel.collected)} />
            </Stats>
          </Panel>

          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Top customers" onExport={() => exportCsv(`top-customers_${tag}.csv`, ['Customer', 'Invoices', 'Billed (excl. tax)'], data.profitability.topCustomers.map((c) => [c.name, c.invoices, c.amount]))}>
              <MiniTable head={['Customer', 'Invoices', 'Billed (excl. tax)']} rows={data.profitability.topCustomers.map((c) => [c.name, c.invoices, money(c.amount)])} />
            </Panel>
            <Panel title="Expenses by category" onExport={() => exportCsv(`expenses_${tag}.csv`, ['Account', 'Name', 'Amount'], data.profitability.expenseByCategory.map((x) => [x.code, x.name, x.amount]))}>
              <MiniTable head={['Account', 'Amount']} rows={data.profitability.expenseByCategory.map((x) => [`${x.code} · ${x.name}`, money(x.amount)])} />
            </Panel>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Receivables" onExport={() => exportCsv('receivables-ageing.csv', ['Bucket', 'Amount'], data.receivables.buckets.map((b) => [b.label, b.amount]))}>
              <Stats>
                <Stat label="Outstanding" value={money(data.receivables.outstanding)} />
                <Stat label="Overdue" value={pct(data.receivables.overduePct)} tone={data.receivables.overduePct > 30 ? 'bad' : undefined} hint={money(data.receivables.overdue)} />
                <Stat label="DSO" value={data.receivables.dso === null ? '—' : `${data.receivables.dso} days`} hint="Days sales outstanding" />
                <Stat label="Collection rate" value={pct(data.receivables.collectionRate)} hint="Collected ÷ billed" />
              </Stats>
              <AgeingChart buckets={data.receivables.buckets} money={money} />
              <MiniTable head={['Top debtors', 'Overdue', 'Outstanding']} rows={data.receivables.top.map((t) => [t.name, money(t.overdue), money(t.total)])} />
            </Panel>
            <Panel title="Payables" onExport={() => exportCsv('payables-ageing.csv', ['Bucket', 'Amount'], data.payables.buckets.map((b) => [b.label, b.amount]))}>
              <Stats>
                <Stat label="Outstanding" value={money(data.payables.outstanding)} />
                <Stat label="Due this week" value={money(data.payables.dueThisWeek)} hint="Includes overdue" />
                <Stat label="DPO" value={data.payables.dpo === null ? '—' : `${data.payables.dpo} days`} hint="Days payables outstanding" />
                <Stat label="Overdue" value={pct(data.payables.overduePct)} hint={money(data.payables.overdue)} />
              </Stats>
              <AgeingChart buckets={data.payables.buckets} money={money} />
              <MiniTable head={['Top vendors', 'Overdue', 'Outstanding']} rows={data.payables.top.map((t) => [t.name, money(t.overdue), money(t.total)])} />
            </Panel>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Tax" onExport={() => exportCsv(`tax_${tag}.csv`, ['Item', 'Amount'], [
              ['Output tax', data.tax.outputTax], ['Input tax credit', data.tax.inputCredit], ['Net GST payable', data.tax.netPayable],
              ['Credit carried forward', data.tax.creditCarriedForward], ['TDS payable', data.tax.tdsPayable]])}>
              <Stats>
                <Stat label="Output tax" value={money(data.tax.outputTax)} />
                <Stat label="Input credit" value={money(data.tax.inputCredit)} />
                <Stat label="Net GST payable" value={money(data.tax.netPayable)} hint={data.tax.creditCarriedForward ? `${money(data.tax.creditCarriedForward)} credit carried forward` : undefined} />
                <Stat label="TDS payable" value={money(data.tax.tdsPayable)} />
              </Stats>
              <p className="text-[12px] text-[#706E6B]">
                Next GSTR-3B due {fmtDate(data.tax.gstDueDate)} · next TDS deposit due {fmtDate(data.tax.tdsDueDate)}. GST figures cover {cur} documents in the selected period.
              </p>
            </Panel>
            <Panel title="Sales to cash" onExport={() => exportCsv(`sales-to-cash_${tag}.csv`, ['Stage', 'Count', 'Value'], [
              ['Deals won', data.funnel.dealsWon, data.funnel.dealsWonValue], ['Invoiced', data.funnel.invoicesIssued, data.funnel.invoicedValue],
              ['Collected', '', data.funnel.collected], ['Quotes sent', data.funnel.quotesSent, ''], ['Quote conversion %', '', data.funnel.quoteConversionPct],
              ['Average days to pay', '', data.funnel.avgDaysToPay]])}>
              <MiniTable head={['Stage', 'Count', 'Value']} rows={[
                ['Deals won', data.funnel.dealsWon, money(data.funnel.dealsWonValue)],
                ['Invoiced', data.funnel.invoicesIssued, money(data.funnel.invoicedValue)],
                ['Collected', '—', money(data.funnel.collected)],
              ]} />
              <Stats>
                <Stat label="Quotes sent" value={data.funnel.quotesSent} />
                <Stat label="Quote conversion" value={pct(data.funnel.quoteConversionPct)} />
                <Stat label="Avg days to pay" value={data.funnel.avgDaysToPay ?? '—'} />
              </Stats>
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
